import {
    VideoCodec,
    type ApplianceCodecCapacity,
    type ApplianceLicenseEntitlement,
    type ApplianceLicenseBundleLicenses,
    type GeneralEncoderSettings,
    type ApplianceVariant,
} from './api/v1/types'
import type { TsToolTransportStream } from './tr101Types'

/** Capacity cost of one UHD channel, in HD units. */
export const HD_CHANNELS_PER_UHD = 4

export interface ApplianceCodecWorkload {
    codec?: 'h264' | 'h265' | 'unsupported'
    resolution?: 'hd' | 'uhd' | 'unsupported'
    hevc422?: boolean
    /** AVC 4:2:2 (x264) encode; the installation's entitlement covers it, not the appliance license. */
    avc422?: boolean
}

/** Uses measured video only when one video PID identifies the codec instance unambiguously. */
export function applianceCodecWorkloadFromStream(stream: TsToolTransportStream | undefined): ApplianceCodecWorkload {
    const videos = stream?.pids?.flatMap((pid) => (pid?.streamInfo?.video ? [pid.streamInfo] : [])) ?? []
    if (videos.length !== 1) return {}
    const video = videos[0]
    const codec =
        video.type?.value === 27
            ? 'h264'
            : video.type?.value === 36
              ? 'h265'
              : video.type?.value === undefined
                ? undefined
                : 'unsupported'
    const width = video.video?.videoSize?.horizontal
    const height = video.video?.videoSize?.vertical
    const resolution =
        width && height
            ? width > 4096 || height > 2160
                ? 'unsupported'
                : width > 1920 || height > 1080
                  ? 'uhd'
                  : 'hd'
            : undefined
    const chroma = video.video?.chromaFormat?.value
    const is422 = chroma === undefined ? undefined : chroma === 2
    return {
        codec,
        resolution,
        hevc422: codec === 'h265' ? is422 : false,
        avc422: codec === 'h264' ? is422 : false,
    }
}

/** Encoding codec and chroma are configured; Edge SDI resolution is detected from the signal. */
export function applianceCodecWorkloadFromEncoder(
    settings: GeneralEncoderSettings | undefined
): ApplianceCodecWorkload {
    const codec =
        settings?.videoCodec === VideoCodec.h264
            ? 'h264'
            : settings?.videoCodec === VideoCodec.h265
              ? 'h265'
              : settings?.videoCodec
                ? 'unsupported'
                : undefined
    const is422 = settings?.pixelFormat ? settings.pixelFormat.includes('422') : undefined
    return {
        codec,
        hevc422: codec === 'h265' ? is422 : false,
        avc422: codec === 'h264' ? is422 : false,
    }
}

/** Checks measured/configured rights without confusing feature add-ons with codec capacity. */
export function applianceLicenseShortfalls(
    licenses: ApplianceLicenseBundleLicenses,
    workloads: ApplianceCodecWorkload[],
    asiPorts: number,
    model: ApplianceVariant | undefined
): string[] {
    const reasons: string[] = []
    if (!fitsApplianceCodecCapacity(licenses.encode_decode, workloads))
        reasons.push('Encode/decode usage exceeds licensed codec or HD/UHD capacity')
    if (workloads.some((workload) => workload.hevc422) && !licenses.hevc_422)
        reasons.push('HEVC 4:2:2 requires its feature license in addition to codec capacity')
    if (asiPorts > 0 && !licenses.asi) reasons.push('ASI ports require an ASI license')
    for (const [family, entitlement] of Object.entries(licenses)) {
        if (model && entitlement?.compatibleModels && !entitlement.compatibleModels.includes(model))
            reasons.push(`${family} license is not valid on ${model}`)
    }
    return reasons
}

/** Validates the commercial pool before it can be stored or signed. */
export function validateApplianceCodecCapacity(pool: ApplianceCodecCapacity): void {
    if (
        !pool ||
        !Array.isArray(pool.codecs) ||
        pool.codecs.length === 0 ||
        new Set(pool.codecs).size !== pool.codecs.length ||
        pool.codecs.some((codec) => codec !== 'h264' && codec !== 'h265') ||
        !Number.isSafeInteger(pool.hdChannels) ||
        pool.hdChannels < 1 ||
        !Number.isSafeInteger(pool.uhdChannels) ||
        pool.uhdChannels < 0 ||
        pool.uhdChannels * HD_CHANNELS_PER_UHD > pool.hdChannels
    ) {
        throw new Error('Invalid codec capacity: specify codecs and HD/UHD limits')
    }
}

/** Human-readable rights shared by billing, Network Manager, and the offline appliance UI. */
export function formatApplianceCodecCapacity(pool: ApplianceCodecCapacity): string {
    const codecs = pool.codecs.map((codec) => (codec === 'h264' ? 'AVC/H.264' : 'HEVC/H.265')).join(' + ')
    const capacity =
        pool.uhdChannels > 0
            ? `${pool.hdChannels} HD capacity (max ${pool.uhdChannels} UHD; 1 UHD = ${HD_CHANNELS_PER_UHD} HD)`
            : `${pool.hdChannels} HD only`
    return `${codecs}: ${capacity}`
}

export function formatApplianceLicenseEntitlement(entitlement: ApplianceLicenseEntitlement): string {
    if (entitlement.entitlementMetric === 'featureFlag')
        return entitlement.entitlementValue > 0 ? 'Enabled' : 'Disabled'
    return entitlement.codecPools?.map(formatApplianceCodecCapacity).join('; ') ?? 'Missing codec capacity'
}

/**
 * Finds a capacity assignment without lending AVC-only or HD-only rights to another pool.
 * HD and UHD share each pool's budget, with one UHD consuming four HD units.
 * Unknown formats are checked optimistically for provisioning; callers must report them as unverified.
 */
export function fitsApplianceCodecCapacity(
    entitlement: ApplianceLicenseEntitlement | undefined,
    workloads: ApplianceCodecWorkload[]
): boolean {
    if (!entitlement?.codecPools) return workloads.length === 0
    const pools = entitlement.codecPools
    if (workloads.length > entitlement.entitlementValue) return false
    const sorted = [...workloads].sort(
        (a, b) =>
            Number(b.resolution === 'uhd') - Number(a.resolution === 'uhd') || Number(!!b.codec) - Number(!!a.codec)
    )
    const used = pools.map(() => ({ hd: 0, uhd: 0 }))
    const failed = new Set<string>()
    const assign = (index: number): boolean => {
        if (index === sorted.length) return true
        const key = `${index}:${used.map(({ hd, uhd }) => `${hd},${uhd}`).join(';')}`
        if (failed.has(key)) return false
        const workload = sorted[index]
        for (const [poolIndex, pool] of pools.entries()) {
            if (workload.codec === 'unsupported' || workload.resolution === 'unsupported') continue
            if (workload.codec && !pool.codecs.includes(workload.codec)) continue
            const usage = used[poolIndex]
            for (const kind of workload.resolution ? [workload.resolution] : (['hd', 'uhd'] as const)) {
                usage[kind]++
                const fits =
                    usage.uhd <= pool.uhdChannels && usage.hd + HD_CHANNELS_PER_UHD * usage.uhd <= pool.hdChannels
                if (fits && assign(index + 1)) return true
                usage[kind]--
            }
        }
        failed.add(key)
        return false
    }
    return assign(0)
}
